// Reading and rewriting the LOAD prescribed by a session — the weights and
// %1RM figures the three load editors all write through (issue #76).
//
// Load lives on the plan tree, never in a parallel "actuals" log: a session's
// `plan_data` IS the record of what was lifted, so correcting a weight and
// authoring one are the same operation. That keeps the copied summary, the
// history detail and the Coach outline reading a single number, and it means
// the per-set machinery (`setScheme`) already carries "I dropped to 60 kg for
// the last two sets" without inventing a second shape for it.
//
// Pure — every function returns a new plan (or the same reference when the edit
// is a no-op), so callers can drop them into a state updater.

import { cloneDeep, get } from "lodash";
import { enclosingSetCount } from "./planEditing";
import { isHiddenInSummary } from "./planVisuals";
import { isRepUnit } from "./planNodeUtils";
import { seedSetCells, resolveSetCells } from "./setSchemeEditing";

// The two ways to prescribe load. Mutually exclusive by schema — `intensityPct`
// shadows `weight` when both are present — so writing one always clears the
// other (docs/schema.md, and the same rule ExerciseDetailDialog enforces).
export const LOAD_DIMENSIONS = ["weight", "intensityPct"];

export const LOAD_UNIT_SUFFIX = { weight: "kg", intensityPct: "% 1RM" };

/** Which dimension a node currently prescribes, or null for an unloaded node. */
export const nodeLoadDimension = (node) => {
  for (const dim of LOAD_DIMENSIONS) {
    const stat = Number(node?.[dim]);
    if (Number.isFinite(stat) && stat > 0) return dim;
    if (Array.isArray(node?.setScheme?.[dim])) return dim;
  }
  return null;
};

const childPath = (indexPath) =>
  indexPath.map((i) => `children[${i}]`).join(".");

const RECOVERY_TYPES = new Set(["rest", "setrest", "transition"]);

const isRecovery = (node) =>
  RECOVERY_TYPES.has(String(node?.type || "").toLowerCase());

/**
 * Every leaf in the plan that can sensibly carry a load, in visit order.
 *
 * "Can sensibly" means a Work-like leaf that either already prescribes a load
 * or is rep-based — the shape of a lifted set. A timed bodyweight hold with no
 * load stays out, so a circuit session's recap isn't a wall of empty inputs;
 * one that DOES carry a weight (a loaded carry, a weighted plank) is included
 * because the figure is already there to correct.
 *
 * Each entry carries the `indexPath` the edit helpers address, and the set
 * count from the enclosing repeat Group so a per-set editor can size itself.
 */
export const collectLoadableLeaves = (plan) => {
  const out = [];

  const walk = (node, indexPath) => {
    if (!node || isHiddenInSummary(node)) return;
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach((child, i) => walk(child, [...indexPath, i]));
      return;
    }
    if (isRecovery(node)) return;
    // REPS specifically, not every counted unit: a calorie target is an ergo
    // machine, never a lifted set, so it would only add an empty kg input to
    // the recap — the "wall of empty inputs" this filter exists to prevent.
    // One carrying a weight already (a loaded carry) still qualifies above.
    if (nodeLoadDimension(node) === null && !isRepUnit(node.unit)) return;
    out.push({
      node,
      indexPath,
      setCount: enclosingSetCount(plan, indexPath),
    });
  };

  (plan?.children || []).forEach((child, i) => walk(child, [i]));
  return out;
};

// Commit one dimension's resolved (static, array) pair onto a node under the
// schema's rules: only a genuinely varying dimension keeps an array, the static
// field mirrors set 1, and the opposite load dimension is removed so the stored
// figure is the one that actually takes effect.
const writeLoad = (node, dim, staticValue, array) => {
  const other = LOAD_DIMENSIONS.find((d) => d !== dim);

  if (staticValue === null) {
    for (const d of LOAD_DIMENSIONS) {
      delete node[d];
      if (node.setScheme) delete node.setScheme[d];
    }
  } else {
    node[dim] = staticValue;
    if (array) {
      node.setScheme = { ...(node.setScheme || {}), [dim]: array };
    } else if (node.setScheme) {
      delete node.setScheme[dim];
    }
    delete node[other];
    if (node.setScheme) delete node.setScheme[other];
  }

  if (node.setScheme && Object.keys(node.setScheme).length === 0) {
    delete node.setScheme;
  }
};

// null / "" clears the load; anything non-positive or unparseable is rejected
// so a stray keystroke can never write a zero-weight prescription.
const parseLoad = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined; // invalid
  return parsed;
};

const withNode = (plan, indexPath, mutate) => {
  if (!plan || !Array.isArray(indexPath) || indexPath.length === 0) return plan;
  const next = cloneDeep(plan);
  const node = get(next, childPath(indexPath));
  if (!node) return plan;
  return mutate(next, node) === false ? plan : next;
};

/**
 * Write a load onto one leaf.
 *
 * `scope` decides how far the figure reaches, which is the whole point of
 * editing load mid-session:
 *   • "all"    — every set runs this load. Clears the per-set array; the static
 *                field alone is the prescription.
 *   • "set"    — only `setIndex`. The other sets keep whatever they had.
 *   • "onward" — `setIndex` and every set after it, the shape of "that was too
 *                heavy, dropping for the rest".
 *
 * A `value` of null clears the load entirely (bodyweight after all).
 */
export const setNodeLoad = (
  plan,
  indexPath,
  { dim = "weight", value, setIndex = 0, scope = "all" } = {},
) => {
  if (!LOAD_DIMENSIONS.includes(dim)) return plan;
  const parsed = parseLoad(value);
  if (parsed === undefined) return plan;

  return withNode(plan, indexPath, (next, node) => {
    if (parsed === null) {
      writeLoad(node, dim, null, null);
      return true;
    }

    const setCount = enclosingSetCount(next, indexPath);

    // One set, or a change meant for all of them: the static field IS the
    // prescription and any array would only contradict it.
    if (scope === "all" || setCount <= 1) {
      writeLoad(node, dim, parsed, null);
      return true;
    }

    const idx = Number.isFinite(setIndex)
      ? Math.max(0, Math.floor(setIndex))
      : 0;
    if (idx >= setCount) return false;

    // Seed the full row from what this node prescribes today, so sets the edit
    // doesn't name keep their own figures rather than inheriting set 1's.
    const cells = seedSetCells(node.setScheme?.[dim], node[dim], setCount);
    for (let i = 0; i < cells.length; i++) {
      if (scope === "set" ? i === idx : i >= idx) cells[i] = String(parsed);
    }

    const { value: staticValue, array } = resolveSetCells(cells, parsed);
    writeLoad(node, dim, staticValue, array);
    return true;
  });
};

/**
 * Write a whole per-set row at once — the shape a grid of set cells produces.
 *
 * `cells` is one entry per set (strings, since they back controlled inputs); a
 * blank cell falls back to the row's first usable figure rather than punching a
 * hole in the prescription. An all-blank row clears the load.
 */
export const setNodeLoadRow = (plan, indexPath, { dim = "weight", cells }) => {
  if (!LOAD_DIMENSIONS.includes(dim)) return plan;
  if (!Array.isArray(cells) || cells.length === 0) return plan;

  return withNode(plan, indexPath, (next, node) => {
    const fallback = cells.map(parseLoad).find((n) => typeof n === "number");
    if (fallback === undefined) {
      writeLoad(node, dim, null, null);
      return true;
    }
    const { value: staticValue, array } = resolveSetCells(cells, fallback);
    writeLoad(node, dim, staticValue ?? null, array);
    return true;
  });
};

/**
 * Seed the per-set editing row for one node's load — `setCount` cells, each
 * taking its own figure from the scheme where it has one and the static field
 * everywhere else.
 */
export const seedLoadCells = (node, dim, setCount) =>
  seedSetCells(node?.setScheme?.[dim], node?.[dim], setCount);

/**
 * The lodash-style path the compiler stamps on a queue item
 * (["children", 0, "children", 1]) reduced to the index path the edit helpers
 * take ([0, 1]). The Coach addresses the running phase by the former and edits
 * it by the latter, so this is the join between them.
 */
export const treePathToIndexPath = (sourceTreePath) => {
  if (!Array.isArray(sourceTreePath)) return [];
  const out = [];
  for (let i = 1; i < sourceTreePath.length; i += 2) out.push(sourceTreePath[i]);
  return out;
};
